import { Component, Watch } from 'vue-property-decorator';
import { isSameDay, parseISO } from 'date-fns';
import AbstractView from '../abstract-view';
import ActiveSettingsInterface from '@vertical-plus/vue-js-components/lib/model/vos/report/active-settings-interface';
import EventBus from '@vertical-plus/vue-js-core/dist/util/event/event-bus';
import Timesheet from '@/model/entity/audit/timesheet';
import TimesheetBreakdown from '@/components/timesheet-breakdown/index.vue';
import TimesheetDataTableReport from '@/model/service/audit/report/timesheet-data-table-report';
import TimesheetDto from '@/model/entity/audit/timesheet-dto';
import TimesheetForm from '@/components/timesheet-form/index.vue';
import TimesheetService from '@/model/service/audit/timesheet-service';

@Component({
    components:
    {
        'app-timesheet-breakdown': TimesheetBreakdown,
        'app-timesheet-form': TimesheetForm,
    },
})
export default class TimesheetsView extends AbstractView
{
    private timesheet: Timesheet|null = null;
    private report: TimesheetDataTableReport|null = null;
    private saving = false;

    private userId: string|null = null;
    private date: Date = new Date();
    private nextDateFrom = new Date();

    $refs!:
    {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        dataTable: any,
    };

    protected get title(): string
    {
        return this.$t('navbar.message.timesheets').toString();
    }

    protected created()
    {
        if (this.$user)
        {
            this.userId = this.$user.id;
        }

        this.resetTimesheet();

        this.report = new TimesheetDataTableReport(
            this.$serviceContainer,
            this.userId,
            this.$user?.id ?? null,
            new Date(),
        );
    }

    /**
     * Returns whether the selected user is the current user
     */

    private get userIsCurrentUser(): boolean
    {
        return !!((this.userId && this.$user && this.userId === this.$user.id));
    }

    @Watch('date')
    private async onDateChanged()
    {
        if (this.userIsCurrentUser)
        {
            this.nextDateFrom = await this.$serviceContainer.timesheetService.getNextDateFrom(this.date, this.report?.results?.results ?? []);
            this.resetTimesheet();
        }
    }

    @Watch('report.results')
    private async onResultsChanged()
    {
        if (this.userIsCurrentUser)
        {
            this.nextDateFrom = await this.$serviceContainer.timesheetService.getNextDateFrom(this.date, this.report?.results?.results ?? []);
            this.resetTimesheet();
        }
    }

    /**
     * Resets the new timesheet
     */

    private resetTimesheet()
    {
        this.timesheet = new Timesheet(new TimesheetDto());

        if (this.$user)
        {
            this.timesheet.userId = this.$user.id;
        }

        this.report?.results?.results;

        this.timesheet.dateFrom = this.nextDateFrom;

        const now = new Date();

        // If same day then set dateTo to now
        if (isSameDay(this.nextDateFrom, now))
        {
            this.timesheet.dateTo = this.$clearSeconds(now);
        }

        // Otherwise, set it to the same time as the dateFrom
        else
        {
            this.timesheet.dateTo = this.$clearSeconds(this.nextDateFrom);
        }
    }

    /**
     * Returns the title for a type value
     *
     * @param value
     * @returns
     */

    private getActivityTitle(value: string): string
    {
        return TimesheetService.getActivityTitle(value);
    }

    /**
     * Returns the chargeable for a type value
     *
     * @param value
     * @returns
     */

    private getChargeableTitle(value: string): string
    {
        return TimesheetService.getChargeableTitle(value);
    }

    /**
     * Handles setting updates
     */

    private onSettingsUpdated(settings: ActiveSettingsInterface)
    {
        this.userId = settings.user as string;
        this.date = parseISO(settings.date as string);

        if (this.report)
        {
            this.report.setUserId(this.userId);
            this.report.setDate(this.date);
        }
    }

    /**
     * Handles form saves
     *
     * @param timesheet
     */

    private async onSave(timesheet: Timesheet)
    {
        try
        {
            this.saving = true;

            await this.$serviceContainer.timesheetService.createTimesheet(timesheet);

            // Update the next date from if the date is later
            if (timesheet.dateTo && timesheet.dateTo > this.nextDateFrom)
            {
                this.nextDateFrom = new Date(timesheet.dateTo.getTime());
            }

            this.$refs.dataTable.reload();

            EventBus.instance.$emit(
                'notification',
                this.$t('general.message.saved_successfully'),
                'success',
            );

            EventBus.instance.$emit('timesheet-updated');

            this.resetTimesheet();
        }
        catch (error: unknown)
        {
            EventBus.instance.$emit(
                'notification',
                this.$t('general.error.failed_saving_server_error'),
                'error',
            );
        }
        finally
        {
            this.saving = false;
        }
    }

    /**
     * Handles timesheet deletes
     *
     * @param timesheet
     */

    private onDelete(timesheet: Timesheet)
    {
        if (!timesheet.id)
        {
            return;
        }

        EventBus.instance.$emit(
            'confirm-dialog',
            this.$t('general.message.delete_confirmation_title'),
            this.$t('general.message.delete_confirmation_message'),
            () =>
            {
                this.delete(timesheet.id as number);
            },
        );
    }

    /**
     * Deletes a timesheet
     *
     * @param timesheetId
     */

    private async delete(timesheetId: number)
    {
        try
        {
            await this.$serviceContainer.timesheetService.deleteTimesheet(timesheetId);

            this.$refs.dataTable.reload();

            EventBus.instance.$emit(
                'notification',
                this.$t('general.message.deleted'),
                'success',
            );

            EventBus.instance.$emit('timesheet-updated');
        }
        catch (error: unknown)
        {
            EventBus.instance.$emit(
                'notification',
                this.$t('general.error.failed_deleting_server_error'),
                'error',
            );
        }
    }
}